/**
 * errors.ts — traducción central de errores a mensajes amigables (es-PE).
 *
 * Regla: ningún componente muestra err.message crudo. Todo pasa por
 * friendlyMessage(), que mapea HttpError por status, respeta los mensajes
 * humanos que ya manda el API, y detecta caídas de red.
 */
import { HttpError } from "./http-client.js";

/** Contexto opcional para afinar el mensaje (ej. login). */
export type ErrorContext = "login" | "generic";

/** Mensaje del servidor SOLO si trae un `error` legible (no el fallback sintético). */
function serverMessage(err: HttpError): string | null {
  const body = err.body;
  if (body && typeof body === "object" && "error" in body) {
    const msg = (body as { error: unknown }).error;
    if (typeof msg === "string" && msg.trim().length > 0) return msg;
  }
  return null;
}

/** ¿El error es una caída de red (fetch no llegó al servidor)? */
function esErrorDeRed(err: unknown): boolean {
  if (err instanceof HttpError) return false;
  if (err instanceof TypeError) return true; // fetch lanza TypeError al fallar la conexión
  if (err instanceof Error) return /failed to fetch|networkerror|load failed/i.test(err.message);
  return false;
}

const POR_STATUS: Record<number, string> = {
  400: "Hay datos inválidos. Revisa los campos e intenta de nuevo.",
  401: "Tu sesión expiró. Inicia sesión nuevamente.",
  403: "No tienes permiso para realizar esta acción.",
  404: "No encontramos lo que buscas. Puede que el servidor no esté disponible.",
  409: "La operación no se pudo completar por un conflicto con el estado actual.",
  413: "El archivo es demasiado grande.",
  422: "Algunos datos no son válidos. Revisa el formulario.",
  429: "Demasiados intentos. Espera unos segundos e intenta de nuevo.",
};

/**
 * Returns true when the error is a network failure or a server-side (5xx) error,
 * meaning the request may succeed on retry and it is safe to enqueue the operation
 * for later. Returns false for client errors (4xx) that represent invalid input and
 * should be shown to the user instead of silently queued.
 *
 * Used by NuevoInforme (Fix 5) to decide whether to enqueue a failed create.
 */
export function isNetworkOrServerFailure(err: unknown): boolean {
  if (err instanceof HttpError) return err.status >= 500;
  // TypeError = fetch network failure; all other non-HttpError = unknown → enqueue conservatively
  return true;
}

/**
 * Traduce cualquier error a un mensaje amigable para mostrar al usuario.
 * @param err   el error capturado
 * @param ctx   contexto para afinar (ej. "login")
 */
export function friendlyMessage(err: unknown, ctx: ErrorContext = "generic"): string {
  if (esErrorDeRed(err)) {
    return "No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.";
  }

  if (err instanceof HttpError) {
    if (ctx === "login" && err.status === 401) {
      return "Correo o contraseña incorrectos.";
    }
    // Mensajes humanos del API (ej. "Ya enviado, no editable") tienen prioridad.
    const delServidor = serverMessage(err);
    if (delServidor) return delServidor;

    if (err.status >= 500) {
      return "El servidor tuvo un problema. Intenta de nuevo en unos minutos.";
    }
    return POR_STATUS[err.status] ?? "No se pudo completar la operación. Intenta de nuevo.";
  }

  return "Algo salió mal. Intenta de nuevo.";
}
